import {
  ActivityStats,
  DistanceIcon,
  Geogroup,
  GeogroupService,
  Period,
  PlaceIcon,
  SavedCO2Icon,
  TPeriod,
  currentMonth,
  currentWeek,
  currentYear,
  defaultEnvironment,
  periodTypesMap,
  prevMonth,
  useCancellablePromise,
  useUnits,
} from '@geovelo-frontends/commons';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Button from '../../components/button';
import { NextIcon, PrevIcon } from '../../components/icons';
import StatCard from '../../components/stat-card';
import usePeriod from '../../hooks/period';

import CommunityChart from './chart';

function Stats({ geogroup }: { geogroup: Geogroup | undefined }): JSX.Element {
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { toDistance } = useUnits();
  const { getTitle: getPeriodTitle } = usePeriod();
  const [periods, setPeriods] = useState<{ current: Period; prev: Period }>({
    current: currentMonth.clone(),
    prev: prevMonth.clone(),
  });
  const [periodType, setPeriodType] = useState<TPeriod>('month');
  const [activities, setActivities] = useState<ActivityStats[]>();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();

  useEffect(() => {
    return () => cancelPromises();
  }, []);

  useEffect(() => {
    let newCurrentPeriod: Period | undefined;

    switch (periodType) {
      case 'week':
        newCurrentPeriod = currentWeek.locale(currentLanguage);
        break;
      case 'month':
        newCurrentPeriod = currentMonth.locale(currentLanguage);
        break;
      case 'year':
        newCurrentPeriod = currentYear.locale(currentLanguage);
        break;
      default:
        return;
    }

    setPeriods({
      current: newCurrentPeriod,
      prev: newCurrentPeriod.getPrevPeriod(),
    });
  }, [periodType]);

  useEffect(() => {
    if (geogroup) getActivities(geogroup.id);
  }, [periods, geogroup]);

  async function getActivities(id: number) {
    setActivities(undefined);

    try {
      const _activities = await cancellablePromise(
        Promise.all([
          GeogroupService.getActivityStats({ geoGroupId: id }, periods.current.toIPeriod()),
          GeogroupService.getActivityStats({ geoGroupId: id }, periods.prev.toIPeriod()),
        ]),
      );

      setActivities(_activities);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.activities.server_error'), { variant: 'error' });
      }
    }
  }

  function prev() {
    const newCurrentPeriod = periods.current.getPrevPeriod();

    setPeriods({ current: newCurrentPeriod, prev: newCurrentPeriod.getPrevPeriod() });
  }

  function next() {
    const newCurrentPeriod = periods.current.getNextPeriod();

    setPeriods({ current: newCurrentPeriod, prev: periods.current });
  }

  const periodTypes: TPeriod[] = ['week', 'month', 'year'];

  return (
    <>
      <Box display="flex" flexDirection="column" gap={4}>
        <Typography component="h2" fontWeight={700} variant="h6">
          {t('geovelo.navigation.stats')}
        </Typography>
        <Box
          alignContent="center"
          display="flex"
          flexDirection={{ xs: 'column-reverse', md: 'row' }}
          gap={2}
          justifyContent={{ xs: undefined, md: 'space-between' }}
        >
          <Box alignItems="center" display="flex" gap={2}>
            <Tooltip
              placement="bottom"
              title={t('commons.periods.actions.prev', { context: periods.current.type })}
            >
              <IconButton className="prev" onClick={() => prev()} size="small">
                <PrevIcon color="primary" />
              </IconButton>
            </Tooltip>
            <StyledTitle fontWeight={700} periodtype={periods.current.type}>
              {getPeriodTitle(periods.current)}
            </StyledTitle>
            <Tooltip
              placement="bottom"
              title={t('commons.periods.actions.next', { context: periods.current.type })}
            >
              <span>
                <IconButton
                  className="next"
                  disabled={!periods.current.hasNext}
                  onClick={() => next()}
                  size="small"
                >
                  <NextIcon color={!periods.current.hasNext ? 'inherit' : 'primary'} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box display="flex" gap={2}>
            {periodTypes.map((key) => {
              const { titleKey } = periodTypesMap[key];

              return (
                <Button
                  disableElevation
                  disabled={periodType === key}
                  key={key}
                  onClick={() => setPeriodType(key)}
                  sx={
                    periodType === key
                      ? {
                          backgroundColor: 'rgba(194, 213, 243, 0.5)',
                          borderRadius: 5,
                          color: '#326AC2 !important',
                        }
                      : { borderRadius: 5, color: '#666666' }
                  }
                >
                  {t(titleKey)}
                </Button>
              );
            })}
          </Box>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Box width={{ xs: '100%', sm: 'calc((100% - 32px) / 3)' }}>
            <StatCard
              bgColor="#EAE2FC"
              color="#905EFA"
              Icon={PlaceIcon}
              label={t('commons.stats_types.journeys')}
              progression={
                activities && activities.length > 0
                  ? `${activities[0].nbRoutes > activities[1].nbRoutes ? '+ ' : '- '}${t(
                      'geovelo.communities.activities.rides',
                      { count: Math.abs(activities[0].nbRoutes - activities[1].nbRoutes) },
                    )} vs ${getPeriodTitle(periods.prev).toLocaleLowerCase()}`
                  : undefined
              }
              value={
                activities ? (activities.length === 0 ? '-' : activities[0].nbRoutes) : undefined
              }
            />
          </Box>
          <Box width={{ xs: '100%', sm: 'calc((100% - 32px) / 3)' }}>
            <StatCard
              bgColor="#FFF5D0"
              color="#BB8A1A"
              Icon={DistanceIcon}
              label={t('geovelo.communities.profile.stats.distance_label')}
              progression={
                activities && activities.length > 0
                  ? `${activities[0].distance > activities[1].distance ? '+ ' : '- '}${toDistance(
                      Math.abs(activities[0].distance - activities[1].distance),
                    )} vs ${getPeriodTitle(periods.prev).toLocaleLowerCase()}`
                  : undefined
              }
              value={
                activities
                  ? activities.length === 0
                    ? '-'
                    : toDistance(activities[0].distance)
                  : undefined
              }
            />
          </Box>
          <Box width={{ xs: '100%', sm: 'calc((100% - 32px) / 3)' }}>
            <StatCard
              bgColor="#DAF3DF"
              color="#03966A"
              Icon={SavedCO2Icon}
              label={t('commons.routing.detail.co2_saved')}
              progression={
                activities && activities.length > 0
                  ? `${activities[0].distance > activities[1].distance ? '+ ' : '- '}${(
                      (Math.abs(activities[0].distance - activities[1].distance) / 1000) *
                      defaultEnvironment.savedKgOfCO2PerKilometer
                    ).toFixed(1)} kg vs ${getPeriodTitle(periods.prev).toLocaleLowerCase()}`
                  : undefined
              }
              value={
                activities
                  ? activities.length === 0
                    ? '-'
                    : `${(
                        (activities[0].distance / 1000) *
                        defaultEnvironment.savedKgOfCO2PerKilometer
                      ).toFixed(1)} kg`
                  : undefined
              }
            />
          </Box>
        </Box>
        <Box border="1px solid rgba(0, 0, 0, 0.12)" borderRadius="10px" padding={4} width="100%">
          <CommunityChart disablePrevPeriod activities={activities} period={periods.current} />
        </Box>
      </Box>
    </>
  );
}

const StyledTitle = styled(Typography)<{ periodtype?: TPeriod }>`
  width: ${({ periodtype }) =>
    periodtype === 'week' ? '180px' : periodtype === 'month' ? '130px' : '40px'};
  text-align: center;
`;

export default Stats;
